import { Component, OnInit, inject } from '@angular/core';
import { BaseComponent } from '../../../../base/base.component';
import { PageHeaderComponent } from '../../../../core/labels/page-header.component';
import { TabPanelComponent } from '../../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../../core/panels/tab-panel/tab.component';
import { CasualtyComponent } from '../casualty/casualty.component';
import { InjuryWcComponent } from './injury-wc.component';
import { MmiWcComponent } from './mmi-wc.component';
import { OshaWcComponent } from './osha-wc.component';
import { SharpsWcComponent } from './sharps-wc.component';
import { VocRehabWcComponent } from './voc-rehab-wc.component';
import { ClaimRow } from '../../../../../models/api/view/claim-view/claim-row';
import { ClaimKey } from '../../../../../models/api/core/claim-key';
import { OshaData } from '../../../../../models/api/view/claim/details/workers-compensation/osha-data';
import { SharpData } from '../../../../../models/api/view/claim/details/workers-compensation/sharp-data';
import { Snackbar } from '../../../../../services/snackbar/snackbar.service';
import { ActivatedRoute } from '@angular/router';
import { extractTabFromFieldPath } from '../../../../../utilities/tab-navigation.helper';

@Component({
  selector: 'app-workers-compensation',
  imports: [
    PageHeaderComponent,
    TabPanelComponent,
    TabComponent,
    CasualtyComponent,
    InjuryWcComponent,
    MmiWcComponent,
    VocRehabWcComponent,
    SharpsWcComponent,
    OshaWcComponent,
  ],
  template: `
    <app-page-header label="Workers' compensation" />
    <app-tab-panel [value]="initialTab">
      <app-tab label="Event" value="Event">
        <ng-template #content>
          <app-casualty #casualty>
            <ng-template #detail>
              @if (showInjuryCard) {
                <app-injury-wc [injury]="casualty.eventData?.injury" />
              }
            </ng-template> </app-casualty
        ></ng-template>
      </app-tab>

      @if (showMmiTab) {
        <app-tab label="MMI" value="MMI">
          <ng-template #content><app-mmi-wc /> </ng-template>
        </app-tab>
      }

      @if (showVocRehabTab) {
        <app-tab label="Voc rehab" value="Voc Rehab">
          <ng-template #content> <app-voc-rehab-wc /> </ng-template>
        </app-tab>
      }

      @if (showOshaTab) {
        <app-tab label="OSHA" value="OSHA">
          <ng-template #content>
            <app-osha-wc
              [oshaData]="oshaData"
              [isLoading]="oshaDataLoading"
              [hasError]="oshaDataError"
              (retry)="onRetryOshaData()"
            />
          </ng-template>
        </app-tab>
      }

      @if (showSharpsTab) {
        <app-tab label="SHARPS" value="Sharps">
          <ng-template #content>
            <app-sharps-wc [sharpData]="sharpData" />
          </ng-template>
        </app-tab>
      }
    </app-tab-panel>
  `,
  styles: ``,
})
export class WorkersCompensationComponent
  extends BaseComponent
  implements OnInit
{
  private snackbar = inject(Snackbar);
  private route = inject(ActivatedRoute);

  claim: ClaimRow;

  // Tab and card visibility controlled by security tokens
  showMmiTab = false;
  showVocRehabTab = false;
  showOshaTab = false;
  showSharpsTab = false;
  showInjuryCard = false;

  oshaData: OshaData | null = null;
  oshaDataLoading = false;
  oshaDataError = false;
  sharpData: SharpData | null = null;

  // Initial tab to activate based on field search navigation
  initialTab: string | null = null;

  async ngOnInit(): Promise<void> {
    this.claim = this.view.currentClaim();

    if (!this.claim) {
      this.view.hideLoading();
      return;
    }

    // Read fieldPath from query parameter and extract tab using shared utility
    this.route.queryParamMap.subscribe((params) => {
      const fieldPath = params.get('fieldPath');
      if (fieldPath) {
        this.initialTab = extractTabFromFieldPath(fieldPath);
      }
    });

    this.initializeSecurityChecks();
    const claimKey: ClaimKey = {
      recordSource: this.claim.recordSource,
      recordId: this.claim.recordId,
    };
    // Only load OSHA data if user has required security tokens
    if (this.showOshaTab) {
      await this.loadOshaData(claimKey);
    }

    // Only load Sharp data if user has required security tokens
    if (this.showSharpsTab) {
      await this.loadSharpData(claimKey);
    }

    this.view.hideLoading();
  }

  private initializeSecurityChecks(): void {
    const hasDetailToken = this.session.authorize('Details');

    this.showMmiTab = hasDetailToken;
    this.showInjuryCard = hasDetailToken;

    const hasWorkStatusToken = this.session.authorize('WorkStatus');
    const hasVocRehabToken = this.session.authorize('ClaimVocRehab');
    this.showVocRehabTab =
      hasDetailToken && hasWorkStatusToken && hasVocRehabToken;

    const hasOshaToken = this.session.authorize('ClaimOSHA');
    this.showOshaTab = hasDetailToken && hasOshaToken;

    const hasSharpsToken = this.session.authorize('ClaimSHARPS');
    this.showSharpsTab = hasSharpsToken;
  }

  async loadSharpData(claimKey: ClaimKey): Promise<void> {
    if (!this.claim) {
      this.showSharpsTab = false;
      return;
    }

    const response = await this.api.sharp.getSharpData(claimKey);
    this.showSharpsTab = response.hasPurchased;

    if (response.data) {
      this.sharpData = response.data;
      this.sharpData.isFromCalifornia = response.isFromCalifornia;
    }
  }

  async loadOshaData(claimKey: ClaimKey): Promise<void> {
    if (!this.claim) {
      this.showOshaTab = false;
      return;
    }

    this.oshaDataLoading = true;
    this.oshaDataError = false;

    const contractNumber = this.getValidContractNumber();

    try {
      const response = await this.api.osha.getOshaData(
        claimKey,
        contractNumber,
      );

      // Final visibility check: security tokens + purchase flag
      this.showOshaTab = response.hasPurchased;

      if (response.data) {
        this.oshaData = response.data;
      } else {
        this.oshaData = null;
      }
    } catch (error) {
      this.oshaData = null;
      this.showOshaTab = false;
      this.snackbar.show('Failed to load OSHA data', 'Error');
    } finally {
      this.oshaDataLoading = false;
    }
  }

  onRetryOshaData(): void {
    if (!this.claim) {
      return;
    }

    const claimKey: ClaimKey = {
      recordSource: this.claim.recordSource,
      recordId: this.claim.recordId,
    };
    this.loadOshaData(claimKey);
  }

  private getValidContractNumber(): number | undefined {
    if (!this.claim?.contractNumber) {
      return undefined;
    }

    const contractNumber = Number(this.claim.contractNumber);
    return isNaN(contractNumber) ? undefined : contractNumber;
  }
}
